import React, { useEffect, useState } from "react";
import clsx from "clsx";
import checkCurrDomainIsOrgDomain from "helpers/checkCurrDomainIsOrgDomain";
import useOrganization from "../../../hooks/useOrganization";

import {
  JoinCustomDomainOrgBanner,
  JoinOrgBanner,
  SignIn,
  SignInBanner,
  SignInOrg,
  SignInOrgBanner,
  SignUp,
  SignUpBanner,
  SignUpOrg,
} from "./components";
import type { AuthModalTypeT } from "./models/models";
import type { AuthenticationOptionsT } from "models/models";

import styles from "./authentication.module.scss";

interface AuthenticationI extends React.HTMLAttributes<HTMLDivElement> {
  type: AuthModalTypeT;
  subUserAuthOptions?: AuthenticationOptionsT;
  subUserDomain?: string;
  onSubmitSuccess?: (...args: unknown[]) => void;
  onSubUserSubmitSuccess?: (email: string) => void;
}

const Authentication = (props: AuthenticationI) => {
  const {
    type = "sign-in",
    subUserAuthOptions,
    subUserDomain,
    onSubmitSuccess,
    onSubUserSubmitSuccess,
    className,
    ...restProps
  } = props;

  const [modalType, setModalType] = useState<AuthModalTypeT>(type);
  const [modalTypeAdjustedToDomain, setModalTypeAdjustedToDomain] =
    useState<AuthModalTypeT>(type);

  const currDomainIsOrgDomain = checkCurrDomainIsOrgDomain();

  const { fetchOrgAuthOptions, orgAuthOptions, topUserPageLink, orgName } =
    useOrganization();

  useEffect(() => {
    if (currDomainIsOrgDomain) {
      fetchOrgAuthOptions();
    }
  }, [currDomainIsOrgDomain]);

  useEffect(() => {
    if (currDomainIsOrgDomain) {
      if (modalType === "sign-in") {
        setModalTypeAdjustedToDomain("sign-in-org");
        return;
      }

      if (modalType === "join-org") {
        setModalTypeAdjustedToDomain("join-custom-domain-org");
        return;
      }
    } else {
      if (modalType === "sign-in-org") {
        setModalTypeAdjustedToDomain("sign-in");
        return;
      }

      if (modalType === "join-custom-domain-org") {
        setModalTypeAdjustedToDomain("join-org");
        return;
      }

      setModalTypeAdjustedToDomain(modalType);
    }
  }, [currDomainIsOrgDomain, modalType]);

  switch (modalTypeAdjustedToDomain) {
    case "sign-in":
      return (
        <div className={clsx(styles.wrapper, className)} {...restProps}>
          <SignIn
            subUserAuthOptions={subUserAuthOptions}
            onSubmitSuccess={onSubmitSuccess}
            onSubUserSubmitSuccess={onSubUserSubmitSuccess}
          />
          <SignUpBanner setModalType={setModalType} />
        </div>
      );
    case "sign-up":
      return (
        <div className={clsx(styles.wrapper, className)} {...restProps}>
          <SignInBanner setModalType={setModalType} />
          <SignUp
            onSubmitSuccess={onSubmitSuccess}
            onSubUserSubmitSuccess={onSubUserSubmitSuccess}
          />
        </div>
      );
    case "sign-in-org":
      return (
        <div className={clsx(styles.wrapper, className)} {...restProps}>
          <SignInOrgBanner
            setModalType={setModalType}
            topUserPageLink={topUserPageLink}
            orgName={orgName}
          />
          <SignInOrg
            onSubmitSuccess={onSubmitSuccess}
            onSubUserSubmitSuccess={onSubUserSubmitSuccess}
          />
        </div>
      );
    case "join-org":
      return (
        <div className={clsx(styles.wrapper, className)} {...restProps}>
          <JoinOrgBanner
            subUserDomain={subUserDomain}
            setModalType={setModalType}
          />
          <SignUpOrg
            authOptions={subUserAuthOptions}
            onSubmitSuccess={onSubmitSuccess}
            onSubUserSubmitSuccess={onSubUserSubmitSuccess}
          />
        </div>
      );
    case "join-custom-domain-org":
      return (
        <div className={clsx(styles.wrapper, className)} {...restProps}>
          <JoinCustomDomainOrgBanner
            setModalType={setModalType}
            subUserDomain={subUserDomain}
          />
          <SignUpOrg
            authOptions={orgAuthOptions}
            onSubmitSuccess={onSubmitSuccess}
            onSubUserSubmitSuccess={onSubUserSubmitSuccess}
          />
        </div>
      );
  }
};

export default Authentication;
